import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Loader2,
  ToggleLeft,
  ToggleRight,
  Shield,
  UserCheck,
  ChevronDown,
  Mail,
  Phone,
  Trash2,
  Eye,
} from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { Avatar } from '@vidnight/ui';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { http } from '@/network/http';
import { useAuthStore } from '@/features/auth';
import { FormModal, ConfirmModal, MODAL_COLORS } from '@/components/modal';
import {
  useActivateUser,
  useChangeRole,
  useUserDetail,
  useUpdateUser,
} from '../hooks';
import { DeleteUserConfirmModal } from './DeleteUserConfirmModal';
import { useActiveRoles } from '@/features/admin/hooks';
import { localizedName } from '@/utils/localized';
import { triggerHapticFeedback } from '../../admin/utils/adminAnimations';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import toast from 'react-hot-toast';
import type { UserListItem } from '../types';

const FALLBACK_ROLES = ['SUPER_ADMIN', 'ADMIN', 'MANAGER', 'EMP'];

const cardStyle = {
  backgroundColor: MODAL_COLORS.interactive,
  border: `1px solid ${MODAL_COLORS.border}`,
};

const actionBtnStyle = {
  border: `1px solid ${MODAL_COLORS.border}`,
};

interface UserDetailModalProps {
  user: UserListItem;
  onClose: () => void;
}

export function UserDetailModal({ user, onClose }: UserDetailModalProps) {
  const { t, i18n } = useTranslation();
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    fromRole: string;
    toRole: string;
  }>({ open: false, fromRole: '', toRole: '' });
  const [isOpen, setIsOpen] = useState(true);
  const { hasPermission } = usePermissions();
  const requestClose = useCallback(() => setIsOpen(false), []);

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);
  const startImpersonation = useAuthStore((s) => s.startImpersonation);
  const impersonateMutation = useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await http.post<{ user: any; accessToken: string; permissions: string[] }>(`/users/${userId}/impersonate`);
      return data;
    },
    onSuccess: (data) => {
      queryClient.removeQueries({ queryKey: ['auth', 'profile'] });
      startImpersonation(data.user, data.accessToken, data.permissions);
      navigate('/');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || t('common.error');
      toast.error(message);
    },
  });
  const activateUserMutation = useActivateUser();
  const changeRoleMutation = useChangeRole();
  const updateUserMutation = useUpdateUser();
  const { data: activeRoles } = useActiveRoles();
  const roleOptions = activeRoles && activeRoles.length > 0
    ? activeRoles
    : FALLBACK_ROLES.map((code) => ({ code, name: code, nameEn: code, id: code, color: null, icon: null, level: 0 }));

  const { data: userDetail } = useUserDetail(user.id);

  const fullName = (() => {
    if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
    return user.firstName || user.lastName || user.email;
  })();

  const handleToggleActive = async () => {
    try {
      triggerHapticFeedback();
      await activateUserMutation.mutateAsync({
        id: user.id,
        active: user.status !== 'ACTIVE',
      });
      toast.success(
        t(user.status === 'ACTIVE' ? 'users.deactivatedSuccess' : 'users.activatedSuccess')
      );
    } catch {
      toast.error(t('users.updateFailed'));
    }
  };

  const handleRoleChange = (newRole: string) => {
    if (newRole === user.role) {
      setRoleDropdownOpen(false);
      return;
    }
    setConfirmDialog({ open: true, fromRole: user.role, toRole: newRole });
    setRoleDropdownOpen(false);
  };

  const confirmRoleChange = async () => {
    try {
      triggerHapticFeedback();
      await changeRoleMutation.mutateAsync({ id: user.id, role: confirmDialog.toRole });
      setConfirmDialog({ open: false, fromRole: '', toRole: '' });
      toast.success(t('users.roleChangedSuccess'));
    } catch {
      toast.error(t('users.roleChangeFailed'));
    }
  };

  return (
    <>
      <FormModal
        isOpen={isOpen && !isDeleteOpen}
        onClose={requestClose}
        onExitComplete={!isDeleteOpen ? onClose : undefined}
        title={fullName}
        maxWidth="max-w-lg"
      >
        <div className="space-y-2">
          {/* Identity */}
          <div className="flex items-center gap-3 rounded-xl p-3" style={cardStyle}>
            <Avatar src={user.avatarUrl} name={fullName} size="lg" className="!flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5 flex-shrink-0 opacity-50" />
                <p className="text-sm truncate opacity-70">{user.email}</p>
              </div>
              {user.phone && (
                <div className="flex items-center gap-1.5 mt-0.5">
                  <Phone className="w-3.5 h-3.5 flex-shrink-0 opacity-50" />
                  <p className="text-sm opacity-70">{user.phone}</p>
                </div>
              )}
            </div>
          </div>

          {/* Role */}
          <div className="rounded-xl p-3" style={cardStyle}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 opacity-60" />
                <span className="text-sm font-bold">{t('users.role')}</span>
                <span className="text-sm font-semibold opacity-70">{t(`users.roles.${user.role}`)}</span>
              </div>
              {user.role !== 'SUPER_ADMIN' && hasPermission('action:edit_users') && (
                <div className="relative">
                  <button
                    onClick={() => {
                      triggerHapticFeedback();
                      setRoleDropdownOpen(!roleDropdownOpen);
                    }}
                    className="flex items-center gap-1.5 px-2.5 py-1 glass-surface rounded-lg text-xs font-bold hover:bg-white/10 transition-colors cursor-pointer"
                    style={actionBtnStyle}
                  >
                    {t('users.changeRole')}
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                  <AnimatePresence>
                    {roleDropdownOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        className="absolute top-full mt-1 right-0 glass-surface z-50 min-w-[140px] rounded-lg overflow-hidden"
                        style={{ ...actionBtnStyle, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                      >
                        {roleOptions.map((role) => (
                          <button
                            key={role.code}
                            onClick={() => handleRoleChange(role.code)}
                            className={`w-full text-left px-3 py-1.5 text-sm hover:bg-white/10 transition-colors cursor-pointer ${
                              role.code === user.role ? 'font-bold opacity-50' : ''
                            }`}
                          >
                            {localizedName(role, i18n.language)}
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>
          </div>

          {/* Status */}
          <div className="rounded-xl p-3" style={cardStyle}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UserCheck className="w-4 h-4 opacity-60" />
                <span className="text-sm font-bold">{t('users.status')}</span>
                <span className="text-sm font-semibold opacity-70">{t(`users.statuses.${user.status}`)}</span>
              </div>
              {user.status !== 'PENDING' && hasPermission('action:edit_users') && (
                <button
                  onClick={handleToggleActive}
                  disabled={activateUserMutation.isPending}
                  className="flex items-center gap-1.5 px-2.5 py-1 glass-surface rounded-lg text-xs font-bold hover:bg-white/10 transition-colors disabled:opacity-50 cursor-pointer"
                  style={actionBtnStyle}
                >
                  {activateUserMutation.isPending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : user.status === 'ACTIVE' ? (
                    <>
                      <ToggleRight className="w-3.5 h-3.5" />
                      {t('users.deactivate')}
                    </>
                  ) : (
                    <>
                      <ToggleLeft className="w-3.5 h-3.5" />
                      {t('users.activate')}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* View As (Impersonate) */}
          {hasPermission('tab:user_impersonation') && currentUser?.id !== user.id && user.status === 'ACTIVE' && (
            <button
              onClick={() => {
                triggerHapticFeedback();
                impersonateMutation.mutate(user.id);
              }}
              disabled={impersonateMutation.isPending}
              className="flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-xl font-bold hover:translate-x-[1px] hover:translate-y-[1px] transition-transform cursor-pointer disabled:opacity-50"
              style={{
                backgroundColor: 'rgba(219, 234, 254, 0.6)',
                border: `1px solid ${MODAL_COLORS.border}`,
                color: '#1d4ed8',
              }}
            >
              {impersonateMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
              {t('admin.impersonation.viewAs')}
            </button>
          )}

          {/* Delete */}
          {user.role !== 'SUPER_ADMIN' && hasPermission('action:delete_users') && (
            <button
              onClick={() => {
                triggerHapticFeedback();
                setIsDeleteOpen(true);
              }}
              className="flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-xl font-bold hover:translate-x-[1px] hover:translate-y-[1px] transition-transform cursor-pointer disabled:opacity-50"
              style={{
                backgroundColor: 'rgba(254, 226, 226, 0.6)',
                border: `1px solid ${MODAL_COLORS.border}`,
                color: '#B91C1C',
              }}
            >
              <Trash2 className="w-4 h-4" />
              {t('users.delete')}
            </button>
          )}
        </div>
      </FormModal>

      {/* Role Change Confirm Dialog */}
      <ConfirmModal
        isOpen={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        onConfirm={confirmRoleChange}
        title={t('users.confirmRoleChange')}
        message={t('users.confirmRoleChangeMessage', {
          userName: fullName,
          fromRole: t(`users.roles.${confirmDialog.fromRole}`),
          toRole: t(`users.roles.${confirmDialog.toRole}`),
        })}
        variant="warning"
        isPending={changeRoleMutation.isPending}
      />

      <DeleteUserConfirmModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        user={user}
        onDeleted={requestClose}
      />
    </>
  );
}
